import localeEnCA from '@/locales/en-CA.json'
import localeEnClub from '@/locales/en-club.json'
import localeEn from '@/locales/en.json'
import type { CurrencyCode } from '@/types'
import { ThemeCurrency, ThemeType, getTheme } from '@goodlifeplus/themes'
import type { SiteSlug } from '@goodlifeplus/themes/types'
import {
  type DefaultDateTimeFormatSchema,
  type DefaultNumberFormatSchema,
  type Locale,
  createI18n,
} from 'vue-i18n'

type MessageSchema = typeof localeEn.messages
type DeepPartial<T> = { [P in keyof T]?: DeepPartial<T[P]> }

function buildNumberFormats(currencyCode: string): DefaultNumberFormatSchema {
  return {
    number: { style: 'decimal' },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
    currency: {
      style: 'currency',
      currencyDisplay: 'symbol',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    'currency-short': {
      style: 'currency',
      currencyDisplay: 'symbol',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: false,
    },
    percent: {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  }
}

const localeCurrencyMap: Record<string, CurrencyCode> = {
  'en-GB': 'GBP',
  'en-IE': 'EUR',
  'en-CA': 'CAD',
}

const siteLocale = import.meta.env.GLP_LOCALE || 'en-GB'
export const defaultCurrency: CurrencyCode =
  localeCurrencyMap[siteLocale] ?? 'GBP'

export const i18n = createI18n<[MessageSchema], Locale>({
  locale: siteLocale,
  fallbackLocale: 'en-GB',
  legacy: true,
  globalInjection: true,
  warnHtmlMessage: false,
  messages: {
    'en-GB': localeEn.messages,
    'en-IE': localeEn.messages,
    'en-CA': localeEn.messages,
  },
  missing: (locale, key) => {
    console.error(
      'Translation not found! Locale:',
      locale,
      'Translation key:',
      key
    )
  },
  numberFormats: {
    'en-GB': buildNumberFormats(defaultCurrency),
    'en-IE': buildNumberFormats(defaultCurrency),
    'en-CA': buildNumberFormats(defaultCurrency),
  },
  datetimeFormats: {
    'en-GB': localeEn.datetimeFormats as DefaultDateTimeFormatSchema,
    'en-IE': localeEn.datetimeFormats as DefaultDateTimeFormatSchema,
    'en-CA': localeEn.datetimeFormats as DefaultDateTimeFormatSchema,
  },
})

// TODO: reduce conditionals
if (
  getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type === ThemeType.Club
) {
  i18n.global.mergeLocaleMessage(
    'en-GB',
    localeEnClub.messages as Partial<MessageSchema>
  )
}

if (
  getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).currency ===
  ThemeCurrency.CAD
) {
  i18n.global.mergeLocaleMessage(
    'en-GB',
    localeEnCA.messages as DeepPartial<MessageSchema>
  )
}
